import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { CompanyCardComponent } from '../../components/company-card/company-card.component';
import { PaginationComponent } from '../../components/pagination/pagination.component';
import { selectCompanies } from '../../../state/admin.selector';
import { take } from 'rxjs';
import { loadCompanies } from '../../../state/admin.actions';
import { Store } from '@ngrx/store';
import { AsyncPipe } from '@angular/common';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CsvService } from '../../../data/services/csv.service';
import moment from 'moment/moment';
import { NgxDaterangepickerBootstrapDirective } from 'ngx-daterangepicker-bootstrap';
import { DatepickerUtils } from '../../../../../common/helpers/date-picker-utils';

@Component({
  selector: 'app-companies',
  standalone: true,
  imports: [
    CompanyCardComponent,
    PaginationComponent,
    AsyncPipe,
    TranslatePipe,
  ],
  templateUrl: './companies.component.html',
  styleUrl: './companies.component.scss',
})
export class CompaniesComponent implements OnInit {
  @ViewChild(NgxDaterangepickerBootstrapDirective, { static: false })
  pickerDirective!: NgxDaterangepickerBootstrapDirective;
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  selectedDates:
    | { startDate: moment.Moment; endDate: moment.Moment }
    | undefined;
  defaultDateRange = {
    startDate: moment().subtract(1, 'year').startOf('day'),
    endDate: moment().subtract(1, 'day').endOf('day'),
  };
  protected datePickerUtils = inject(DatepickerUtils);
  protected translate = inject(TranslateService);
  protected datepickerLocale = {};
  private store = inject(Store);
  companies$ = this.store.select(selectCompanies);
  private csvService = inject(CsvService);
  private router = inject(Router);

  get isFilterApplied(): boolean {
    if (!this.selectedDates) {
      return false;
    }

    return (
      !this.selectedDates.startDate.isSame(
        this.defaultDateRange.startDate,
        'day',
      ) ||
      !this.selectedDates.endDate.isSame(this.defaultDateRange.endDate, 'day')
    );
  }

  clearFilters(): void {
    this.selectedDates = {
      startDate: moment(this.defaultDateRange.startDate),
      endDate: moment(this.defaultDateRange.endDate),
    };
    if (this.pickerDirective) {
      this.pickerDirective.clear();
    }
  }

  ngOnInit(): void {
    this.datePickerUtils.getDatepickerLocale().subscribe((locale) => {
      this.datepickerLocale = locale;
    });

    this.store
      .select(selectCompanies)
      .pipe(take(1))
      .subscribe((u) => {
        this.store.dispatch(
          loadCompanies({
            page: u.page || 1,
            pageSize: u.pageSize || 10,
            search: u.search,
          }),
        );
      });

    this.translate.onLangChange.subscribe(() => {
      this.updateLocale();
    });
  }

  downloadCompaniesBillingCsv() {
    this.csvService.getCompaniesMonthlyBillingCsv();
  }

  onDateRangeChange(selected: any) {
    if (!selected?.startDate || !selected?.endDate) return;

    const startDate = selected.startDate.startOf('day').toDate();
    const endDate = selected.endDate.endOf('day').toDate();

    this.selectedDates = {
      startDate: moment(startDate),
      endDate: moment(endDate),
    };
  }

  updateLocale() {
    this.datePickerUtils.getDatepickerLocale().subscribe((locale) => {
      this.datepickerLocale = locale;
    });
  }

  onSearchChange(value: string) {
    this.store.dispatch(
      loadCompanies({ page: 1, pageSize: 10, search: value }),
    );
  }

  goToDetails(companyId: string) {
    this.router.navigate(['/admin/companies', companyId]);
  }

  onPageChange(page: number): void {
    this.store
      .select(selectCompanies)
      .pipe(take(1))
      .subscribe((u) => {
        this.store.dispatch(
          loadCompanies({
            page,
            pageSize: u.pageSize,
            search: u.search,
          }),
        );
      });
  }

  onPageSizeChange(newSize: number): void {
    this.store
      .select(selectCompanies)
      .pipe(take(1))
      .subscribe((u) => {
        this.store.dispatch(
          loadCompanies({
            page: 1,
            pageSize: newSize,
            search: u.search,
          }),
        );
      });
  }
}
